// src/components/team/RolesSection.tsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import { saveRoles } from '../../features/roles/rolesSlice';
import { getAuthContext } from '../../utils/jwt';
import AddRoleModal from './AddRoleModal';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from '../../utils/constants';

type RolesSectionProps = {
  ownerRole: string;
  onChangeState: (
    hasChanges: boolean,
    handleReset: () => void,
    handleSave: () => void
  ) => void;
};

// Updated PermissionResponse to handle both old and new structures
type PermissionResponse =
  | {
    categories?: Record<string, string[]>;  // Old structure
    [key: string]: any;
  }
  | any[];  // New structure: array of modules

type Role = {
  id: string;
  tenantId: string;
  name: string;
  systemRole: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  version: number;
  archived: boolean;
  deletedAt: string | null;
};

type RolesResponse = {
  roles: Role[];
};

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Roles that cannot be deleted
const PROTECTED_ROLES = new Set(['Owner', 'Admin', 'Marketer', 'Designer']);

// Map API permission keys to Redux state keys (unique mapping)
const apiToReduxKeyMap: Record<string, string> = {
  'campaigns.create': 'createCampaign',
  'campaigns.edit': 'editCampaign',
  'automations.create': 'createAutomation',
  'automations.edit': 'editAutomation',
  'templates.create': 'createTemplates',
  'templates.view': 'viewTemplates',
  'templates.delete': 'deleteTemplates',
  'templates.duplicate': 'duplicateTemplates',
  'templates.send_test': 'sendTestEmails',
  'team.invite': 'inviteMember',
  'team.members.manage': 'manageMembers',
  'team.roles.manage': 'manageRoles',
  'team.ownership.transfer': 'transferOwnership',
  'team.ownership.request': 'requestOwnership',
  'campaigns.VIEW': 'viewCampaigns',
  'campaigns.CREATE': 'createCampaign',
  'campaigns.EDIT': 'editCampaign',
  'campaigns.DELETE': 'deleteCampaigns',
  'automations.VIEW': 'viewAutomations',
  'automations.CREATE': 'createAutomation',
  'automations.EDIT': 'editAutomation',
  'automations.DELETE': 'deleteAutomations',
  'templates.VIEW': 'viewTemplates',
  'templates.CREATE': 'createTemplates',
  'templates.EDIT': 'editTemplates',
  'templates.DELETE': 'deleteTemplates',
  'templates.DUPLICATE': 'duplicateTemplates',
  'templates.SEND_TEST': 'sendTestEmails',
  'team.INVITE': 'inviteMember',
  'team.MEMBERS_MANAGE': 'manageMembers',
  'team.ROLES_MANAGE': 'manageRoles',
  'team.OWNERSHIP_TRANSFER': 'transferOwnership',
  'team.OWNERSHIP_REQUEST': 'requestOwnership',
};

const reduxToDisplayLabel: Record<string, string> = {
  viewTemplates: 'View Templates',
  createTemplates: 'Create Templates',
  editTemplates: 'Edit Templates',
  updateTemplates: 'Update Templates',
  deleteTemplates: 'Delete Templates',
  duplicateTemplates: 'Duplicate Templates',
  sendTestEmails: 'Send Test Emails',
  createCampaign: 'Create Campaign',
  editCampaign: 'Edit Campaign',
  viewCampaigns: 'View Campaigns',
  deleteCampaigns: 'Delete Campaigns',
  createAutomation: 'Create Automation',
  editAutomation: 'Edit Automation',
  viewAutomations: 'View Automations',
  deleteAutomations: 'Delete Automations',
  inviteMember: 'Invite Team Member',
  manageMembers: 'Manage Members',
  manageRoles: 'Manage Roles',
  transferOwnership: 'Transfer Ownership',
  requestOwnership: 'Request Ownership',
};

const RolesSection: React.FC<RolesSectionProps> = ({
  ownerRole,
  onChangeState,
}) => {
  const dispatch = useDispatch();

  const [roleMatrix, setRoleMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [initialMatrix, setInitialMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);

  const [permissionsData, setPermissionsData] = useState<PermissionResponse | null>(null);
  const [rolesData, setRolesData] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // State for Add Role modal
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);

  const handleResetRef = useRef<() => void>(() => { });
  const handleSaveRef = useRef<() => void>(() => { });

  const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

  const matricesEqual = (
    a: Record<string, Record<string, boolean>>,
    b: Record<string, Record<string, boolean>>
  ): boolean => JSON.stringify(a) === JSON.stringify(b);

  const getApiPermissionsForRole = (reduxKeys: string[]): string[] => {
    const reverseMap: Record<string, string> = {};
    Object.entries(apiToReduxKeyMap).forEach(([apiKey, reduxKey]) => {
      if (!reverseMap[reduxKey]) {
        reverseMap[reduxKey] = apiKey.toLowerCase();
      }
    });

    return reduxKeys
      .filter((key) => reverseMap[key])
      .map((key) => reverseMap[key]);
  };

  const getReduxKeyForPermission = (apiKey: string): string | undefined => {
    if (apiToReduxKeyMap[apiKey]) {
      return apiToReduxKeyMap[apiKey];
    }
    const lowerKey = apiKey.toLowerCase();
    for (const [key, value] of Object.entries(apiToReduxKeyMap)) {
      if (key.toLowerCase() === lowerKey) {
        return value;
      }
    }
    return undefined;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const [permissionsResponse, rolesResponse] = await Promise.all([
        fetch(`${BASE_URL}/v1/team/permissions/catalog`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${BASE_URL}/v1/team/roles`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!permissionsResponse.ok) {
        if (permissionsResponse.status === 403)
          throw new Error('You do not have permission to view permissions');
        throw new Error(`Failed to fetch permissions: ${permissionsResponse.status}`);
      }
      if (!rolesResponse.ok) {
        if (rolesResponse.status === 403)
          throw new Error('You do not have permission to view roles');
        throw new Error(`Failed to fetch roles: ${rolesResponse.status}`);
      }

      const permissionsData = await permissionsResponse.json();
      const rolesData: RolesResponse = await rolesResponse.json();

      setPermissionsData(permissionsData);
      setRolesData(rolesData.roles);

      initializeRoleMatrix(rolesData.roles, permissionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      // console.error('Error fetching data:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: initializeRoleMatrix function to handle both old and new data structures
  const initializeRoleMatrix = (roles: Role[], permissionsData: PermissionResponse) => {
    let allPermissions: string[] = [];

    if (permissionsData && typeof permissionsData === 'object' && 'categories' in permissionsData) {
      const categories = permissionsData.categories;
      if (categories && typeof categories === 'object') {
        Object.values(categories).forEach((perms) => {
          if (Array.isArray(perms)) {
            allPermissions.push(...perms);
          }
        });
      }
    } else if (Array.isArray(permissionsData)) {
      permissionsData.forEach((module) => {
        if (module.permissions && Array.isArray(module.permissions)) {
          module.permissions.forEach((perm: any) => {
            if (perm.code) {
              allPermissions.push(perm.code);
            }
          });
        }
      });
    } else {
      console.warn('Unknown permissions data structure:', permissionsData);
      const extractPermissions = (obj: any): string[] => {
        const perms: string[] = [];
        if (Array.isArray(obj)) {
          obj.forEach(item => {
            if (item.permissions && Array.isArray(item.permissions)) {
              item.permissions.forEach((p: any) => {
                if (p.code) perms.push(p.code);
              });
            }
          });
        }
        return perms;
      };
      allPermissions = extractPermissions(permissionsData);
    }

    const reduxKeys = new Set<string>();
    allPermissions.forEach((apiKey) => {
      const reduxKey = getReduxKeyForPermission(apiKey);
      if (reduxKey) reduxKeys.add(reduxKey);
    });

    const roleNames = roles.map((r) => r.name);

    const matrix: Record<string, Record<string, boolean>> = {};
    reduxKeys.forEach((key) => {
      matrix[key] = {};
      roleNames.forEach((name) => {
        matrix[key][name] = false;
      });
    });

    roles.forEach((role) => {
      role.permissions.forEach((apiKey) => {
        const reduxKey = getReduxKeyForPermission(apiKey);
        if (reduxKey && matrix[reduxKey]) {
          matrix[reduxKey][role.name] = true;
        }
      });
    });

    const snapshot = deepClone(matrix);
    setRoleMatrix(snapshot);
    setInitialMatrix(snapshot);
    setHasChanges(false);
    setIsInitialized(true);
  };

  useEffect(() => {
    if (!isInitialized) return;

    const changed = !matricesEqual(roleMatrix, initialMatrix);
    setHasChanges(changed);
  }, [roleMatrix, initialMatrix, isInitialized]);

  const handleReset = useCallback(() => {
    setRoleMatrix(deepClone(initialMatrix));
    setHasChanges(false);
    setSaveError(null);
  }, [initialMatrix]);

  const handleSave = useCallback(async () => {
    if (saving) return;

    try {
      setSaving(true);
      setSaveError(null);

      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const changedRoles: string[] = [];
      const roleNames = getRoleNames();

      roleNames.forEach((roleName) => {
        let roleChanged = false;

        Object.keys(roleMatrix).forEach((reduxKey) => {
          const currentValue = roleMatrix[reduxKey]?.[roleName] || false;
          const initialValue = initialMatrix[reduxKey]?.[roleName] || false;

          if (currentValue !== initialValue) {
            roleChanged = true;
          }
        });

        if (roleChanged) {
          changedRoles.push(roleName);
        }
      });

      if (changedRoles.length === 0) {
        setHasChanges(false);
        setSaving(false);
        return;
      }

      const roleIdMap: Record<string, string> = {};
      rolesData.forEach((role) => {
        roleIdMap[role.name] = role.id;
      });

      const savePromises = changedRoles.map(async (roleName) => {
        const checkedPermissions: string[] = [];

        Object.keys(roleMatrix).forEach((reduxKey) => {
          if (roleMatrix[reduxKey]?.[roleName]) {
            checkedPermissions.push(reduxKey);
          }
        });

        const apiPermissions = getApiPermissionsForRole(checkedPermissions);

        const roleId = roleIdMap[roleName];
        if (!roleId) {
          throw new Error(`Role ID not found for role: ${roleName}`);
        }

        const url = `${BASE_URL}/v1/team/roles/${roleId}`;
        const body = {
          permissions: apiPermissions,
          name: roleName,
        };

        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(
            `Failed to update role "${roleName}": ${response.status} - ${errorData}`
          );
        }

        return response.json();
      });

      await Promise.all(savePromises);

      const snapshot = deepClone(roleMatrix);
      setInitialMatrix(snapshot);
      setHasChanges(false);

      dispatch(saveRoles(roleMatrix));

      toast.success(`Successfully updated ${changedRoles.length} role(s)`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save roles');
      toast.error(err instanceof Error ? err.message : 'Failed to save roles');
      // console.error('Error saving roles:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [roleMatrix, initialMatrix, rolesData, saving, dispatch]);

  handleResetRef.current = handleReset;
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!isInitialized) return;

    onChangeState(
      hasChanges,
      () => handleResetRef.current(),
      () => handleSaveRef.current()
    );
  }, [hasChanges, isInitialized, onChangeState]);

  const togglePermission = (reduxKey: string, role: string) => {
    if (ownerRole === 'Admin' && (role === 'Admin' || role === 'Owner')) return;

    setRoleMatrix((prev) => ({
      ...prev,
      [reduxKey]: {
        ...prev[reduxKey],
        [role]: !prev[reduxKey]?.[role],
      },
    }));
  };

  // ✅ UPDATED: getGroupedPermissions now returns categories in alphabetical order
  // (case-insensitive). This guarantees templates/team/etc always appear in the
  // same stable order regardless of the API response order.
  const getGroupedPermissions = (): Record<string, string[]> => {
    if (!permissionsData) return {};

    const grouped: Record<string, string[]> = {};

    if (Array.isArray(permissionsData)) {
      // New structure: array of modules
      permissionsData.forEach((module) => {
        const category = module.module || 'Other';
        const mapped: string[] = [];
        const seen = new Set<string>();

        if (module.permissions && Array.isArray(module.permissions)) {
          module.permissions.forEach((perm: any) => {
            if (perm.code) {
              const reduxKey = getReduxKeyForPermission(perm.code);
              if (reduxKey && !seen.has(reduxKey)) {
                seen.add(reduxKey);
                mapped.push(reduxKey);
              }
            }
          });
        }

        if (mapped.length > 0) {
          grouped[category] = mapped;
        }
      });
    } else if (permissionsData && typeof permissionsData === 'object' && 'categories' in permissionsData) {
      // Old structure: { categories: { "module": ["perm1", "perm2"] } }
      const categories = permissionsData.categories;
      if (categories && typeof categories === 'object') {
        Object.entries(categories).forEach(([category, permissions]) => {
          const mapped: string[] = [];
          const seen = new Set<string>();

          if (Array.isArray(permissions)) {
            permissions.forEach((apiKey) => {
              const reduxKey = getReduxKeyForPermission(apiKey);
              if (reduxKey && !seen.has(reduxKey)) {
                seen.add(reduxKey);
                mapped.push(reduxKey);
              }
            });
          }

          if (mapped.length > 0) grouped[category] = mapped;
        });
      }
    } else {
      console.warn('Unknown permissions data structure in getGroupedPermissions:', permissionsData);
    }

    // ✅ Sort categories alphabetically (case-insensitive, stable).
    // This ensures deterministic rendering order:
    //   automations → campaigns → team → templates
    const sortedGrouped: Record<string, string[]> = {};
    Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((key) => {
        sortedGrouped[key] = grouped[key];
      });

    return sortedGrouped;
  };

  const getRoleNames = (): string[] => {
    if (rolesData.length === 0) return ['Owner', 'Admin', 'Designer', 'Marketer'];
    const roleNames = rolesData.map((r) => r.name);
    const sorted = [
      'Owner',
      'Admin',
      ...roleNames.filter((r) => r !== 'Owner' && r !== 'Admin').sort(),
    ];
    return sorted;
  };

  // Open delete confirmation modal
  const openDeleteModal = (roleName: string) => {
    if (PROTECTED_ROLES.has(roleName)) return;
    setRoleToDelete(roleName);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deletingRoleId) return; // don't close while deleting
    setDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  // Confirm delete – actual API call
  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;
    if (PROTECTED_ROLES.has(roleToDelete)) return;

    const role = rolesData.find((r) => r.name === roleToDelete);
    if (!role) {
      toast.error(`Role "${roleToDelete}" not found`);
      closeDeleteModal();
      return;
    }

    try {
      setDeletingRoleId(role.id);

      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const response = await fetch(`${BASE_URL}/v1/team/roles/${role.id}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to delete this role');
        }
        const errorData = await response.text();
        throw new Error(`Failed to delete role: ${response.status} - ${errorData}`);
      }

      toast.success(`Role "${roleToDelete}" deleted successfully`);
      setDeleteModalOpen(false);
      setRoleToDelete(null);
      await fetchData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete role');
      // console.error('Error deleting role:', err);
    } finally {
      setDeletingRoleId(null);
    }
  };

  // Handle creating a new role
  const handleCreateRole = async (roleName: string, permissions: string[]) => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const loadingToast = toast.loading('Creating role...');

      const response = await fetch(`${BASE_URL}/v1/team/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: roleName,
          permissions: permissions,
        }),
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create role: ${response.status}`);
      }

      const newRole = await response.json();

      toast.success(`Role "${roleName}" created successfully!`);

      // Refresh roles data
      await fetchData();

      return newRole;
    } catch (error: any) {
      toast.error(error.message || 'Failed to create role');
      // console.error('Error creating role:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]" />
        <span className="ml-2 text-gray-500">Loading permissions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md my-4">
        <p className="font-medium">Error loading permissions</p>
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchData}
          className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const groupedPermissions = getGroupedPermissions();
  const roleNames = getRoleNames();

  // Horizontal scroll only when more than 6 role columns
  const hasManyColumns = roleNames.length > 6;

  return (
    <div>
      {saveError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
          <p className="font-medium">Error saving roles</p>
          <p className="text-sm">{saveError}</p>
        </div>
      )}

      {saving && (
        <div className="bg-blue-50 border border-blue-200 text-blue-600 px-4 py-3 rounded-md mb-4">
          <p className="text-sm flex items-center">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />
            Saving roles...
          </p>
        </div>
      )}

      {/* Vertical scroll always; horizontal only if > 6 role columns */}
      <div
        className={`max-h-[60vh] border border-gray-200 rounded-md overflow-y-auto ${hasManyColumns ? 'overflow-x-auto' : 'overflow-x-hidden'
          }`}
      >
        <table
          className={`w-full text-sm border-collapse ${hasManyColumns ? 'min-w-[1400px]' : 'min-w-[600px]'
            }`}
        >
          <thead className="bg-gray-100 text-gray-600 text-[13px]">
            <tr>
              {/* Corner cell – sticks top + left */}
              <th className="px-4 py-3 text-left font-normal sticky top-0 left-0 bg-gray-100 z-40 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                MODULES
              </th>
              {roleNames.map((role) => {
                const canDelete = !PROTECTED_ROLES.has(role);
                const roleObj = rolesData.find((r) => r.name === role);
                const isDeleting = roleObj && deletingRoleId === roleObj.id;

                return (
                  <th
                    key={role}
                    className="px-4 py-3 text-center font-normal whitespace-nowrap sticky top-0 bg-gray-100 z-30 min-w-[110px] overflow-visible"
                  >
                    <div className="inline-flex items-center justify-center gap-1">
                      <span>{role}</span>
                      {canDelete && (
                        <span className="relative group/delete inline-flex">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(role);
                            }}
                            disabled={!!deletingRoleId || saving}
                            className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-0.5 rounded"
                            aria-label={`Delete ${role}`}
                          >
                            {isDeleting ? (
                              <span className="inline-block w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FiXComponent size={14} />
                            )}
                          </button>
                          {/* Tooltip below the icon */}
                          <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover/delete:opacity-100 transition-opacity z-50">
                            Delete Role
                          </span>
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              <th
                className="px-4 py-3 text-[#00A4BD] font-normal cursor-pointer hover:text-[#00899E] transition-colors whitespace-nowrap sticky top-0 bg-gray-100 z-30 min-w-[110px]"
                onClick={() => setIsAddRoleModalOpen(true)}
              >
                + Add Role
              </th>
            </tr>
          </thead>

          <tbody>
            {/* ✅ Categories are already sorted alphabetically by getGroupedPermissions */}
            {Object.entries(groupedPermissions).map(([category, permissions]) => (
              <React.Fragment key={category}>
                {/* Category header – label sticks left+top, rest sticks top only */}
                <tr>
                  <td className="px-4 py-2 font-semibold text-gray-700 sticky top-[45px] left-0 bg-gray-50 z-30 border-b min-w-[200px] whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                    {category}
                  </td>
                  {roleNames.map((role) => (
                    <td
                      key={role}
                      className="px-4 py-2 sticky top-[45px] bg-gray-50 z-20 border-b min-w-[110px]"
                    />
                  ))}
                  {/* Empty cell under + Add Role column */}
                  <td className="px-4 py-2 sticky top-[45px] bg-gray-50 z-20 border-b min-w-[110px]" />
                </tr>

                {permissions.map((reduxKey) => {
                  const displayLabel = reduxToDisplayLabel[reduxKey] || reduxKey;
                  const roleValues = roleMatrix[reduxKey] || {};

                  return (
                    <tr key={reduxKey} className="border-b hover:bg-gray-50 group">
                      {/* Left sticky permission name */}
                      <td className="px-4 py-2 text-gray-700 pl-8 sticky left-0 bg-white group-hover:bg-gray-50 z-10 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                        {displayLabel}
                      </td>
                      {roleNames.map((role) => {
                        const isDisabled =
                          (ownerRole === 'Admin' &&
                            (role === 'Admin' || role === 'Owner')) ||
                          saving;
                        return (
                          <td
                            key={role}
                            className="px-4 py-2 text-center bg-white group-hover:bg-gray-50 min-w-[110px]"
                          >
                            <input
                              type="checkbox"
                              checked={!!roleValues[role]}
                              onChange={() => togglePermission(reduxKey, role)}
                              disabled={isDisabled}
                              className={`
                                w-4 h-4 rounded border border-gray-300
                                appearance-none cursor-pointer
                                checked:bg-[#00A4BD] checked:border-[#00A4BD]
                                checked:after:content-['✓'] checked:after:block
                                checked:after:text-white checked:after:font-bold
                                checked:after:text-center checked:after:leading-4
                                disabled:bg-gray-200 disabled:border-gray-300
                                disabled:cursor-not-allowed
                              `}
                            />
                          </td>
                        );
                      })}
                      <td className="bg-white group-hover:bg-gray-50 min-w-[110px]" />
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Role Confirmation Modal */}
      {deleteModalOpen && roleToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-[400px] max-w-[90vw]">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Delete Role</h3>
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={!!deletingRoleId}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <FiXComponent size={20} />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 text-sm">
                Are you sure you want to delete the role{' '}
                <span className="font-semibold text-gray-800">"{roleToDelete}"</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={!!deletingRoleId}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteRole}
                disabled={!!deletingRoleId}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingRoleId ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'OK'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Role Modal */}
      <AddRoleModal
        isOpen={isAddRoleModalOpen}
        onClose={() => setIsAddRoleModalOpen(false)}
        permissionsData={permissionsData}
        existingRoles={roleNames}
        onCreateRole={handleCreateRole}
      />

      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
};

export default RolesSection;